import { useState, useEffect } from "react";

import { Send, ArrowLeft, Mail, KeyRound, UserCheck, Shield, AlertTriangle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { ROUTES } from "@/lib/routes";
import { authClient } from "@/lib/auth-client";

const Tests = () => {
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [passwordResetEmail, setPasswordResetEmail] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [welcomeEmail, setWelcomeEmail] = useState("");
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessMessage, setAccessMessage] = useState("");
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  
  const { execute: invokePublic, isLoading: publicLoading } = useEdgeFunction({ auth: false });
  const navigate = useNavigate();

  useKeyboardNavigation({
    onBack: () => navigate(ROUTES.DASHBOARD),
  });

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data, error } = await invokePublic("check-asn-access", {});
        
        if (error || !data) {
          setHasAccess(false);
          setAccessMessage("Unable to verify network access");
        } else {
          setHasAccess(data.allowed);
          setAccessMessage(data.message || "");
        }
      } catch (error) {
        console.error("Error checking ASN access:", error);
        setHasAccess(false);
        setAccessMessage("Failed to verify network access");
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAccess();
  }, []);

  const handleTestMagicLink = async () => {
    if (!magicLinkEmail) {
      toast.error("Please enter an email address");
      return;
    }

    setMagicLinkLoading(true);
    try {
      const { error } = await authClient.signIn.magicLink({
        email: magicLinkEmail,
        callbackURL: `${window.location.origin}/verify-magic-link`,
      });

      if (error) {
        toast.error(error.message || "Failed to send magic link");
      } else {
        toast.success("Magic link sent successfully!");
      }
    } catch (error: any) {
      console.error("Magic link error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleTestPasswordReset = async () => {
    if (!passwordResetEmail) {
      toast.error("Please enter an email address");
      return;
    }

    const { error } = await authClient.auth.sendPasswordResetEmail({
      email: passwordResetEmail,
    });

    if (error) {
      toast.error(error.message || "Failed to send password reset email");
    } else {
      toast.success("Password reset email sent successfully!");
    }
  };

  const handleTestVerificationEmail = async () => {
    if (!verificationEmail) {
      toast.error("Please enter an email address");
      return;
    }

    // Generate a confirmation URL for testing
    const confirmationUrl = `${window.location.origin}/verify-email?token=test-token-${Date.now()}`;

    const { error } = await invokePublic("send-verification-email", {
      body: { 
        email: verificationEmail,
        confirmationUrl
      },
    });

    if (error) {
      toast.error(error.message || "Failed to send verification email");
    } else {
      toast.success("Verification email sent successfully!");
    }
  };

  const handleTestWelcomeEmail = () => {
    toast.info("Welcome email is an internal-only function. It's automatically sent after user signup and cannot be tested directly from the client.", {
      duration: 5000
    });
  };

  if (isCheckingAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Verifying network access...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.DASHBOARD)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              {accessMessage || "This page is only accessible from authorized networks (Starlink ASN 14593)."}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>

        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-foreground">Test Page</h1>
          <p className="text-muted-foreground">
            Internal testing utilities for development
          </p>
        </div>

        <Separator />

        {/* Magic Link Test */}
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Test Magic Link Email
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Send a test magic link to verify email sending is working
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter test email address"
                value={magicLinkEmail}
                onChange={(e) => setMagicLinkEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !magicLinkLoading) {
                    handleTestMagicLink();
                  }
                }}
                className="flex-1"
              />
              <Button
                onClick={handleTestMagicLink}
                disabled={magicLinkLoading}
                className="min-w-[120px]"
              >
                {magicLinkLoading ? "Sending..." : "Send Test"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Password Reset Test */}
        <Card className="border-orange-500/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-orange-500" />
              Test Password Reset Email
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Send a test password reset email to verify the flow
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter test email address"
                value={passwordResetEmail}
                onChange={(e) => setPasswordResetEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !publicLoading) {
                    handleTestPasswordReset();
                  }
                }}
                className="flex-1"
              />
              <Button
                onClick={handleTestPasswordReset}
                disabled={publicLoading}
                className="min-w-[120px]"
                variant="outline"
              >
                {publicLoading ? "Sending..." : "Send Test"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email Verification Test */}
        <Card className="border-green-500/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-500" />
              Test Verification Email
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Send a test email verification link
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter test email address"
                value={verificationEmail}
                onChange={(e) => setVerificationEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !publicLoading) {
                    handleTestVerificationEmail();
                  }
                }}
                className="flex-1"
              />
              <Button
                onClick={handleTestVerificationEmail}
                disabled={publicLoading}
                className="min-w-[120px]"
                variant="outline"
              >
                {publicLoading ? "Sending..." : "Send Test"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Welcome Email Test */}
        <Card className="border-blue-500/50 opacity-60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Welcome Email (Internal Only)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                This email is automatically sent after user signup and requires internal authentication. It cannot be tested directly from the client for security reasons.
              </AlertDescription>
            </Alert>
            <Button
              onClick={handleTestWelcomeEmail}
              variant="outline"
              className="w-full"
            >
              Why can't I test this?
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Tests;
